import { ref, type Ref } from 'vue';

import { logger } from '@exchange/libs/utils/simple-logger/src';
import type { SubscribeCallbacks } from '@exchange/libs/utils/wss/src';
import { WSIncomingEventTypes } from '@exchange/libs/utils/wss/src/lib/websocket-spot-model';

import type { FundingRateMessage, FundingRateData } from './funding-rate-model';
import fundingRateChannel from './FundingRate.channel';

class FundingRate {
  private readonly logPrefix = 'FundingRateService:';

  private data: Dictionary<{
    fundingRate: Ref<FundingRateData>;
    listenerCount: number;
    unsubscribe?: () => Promise<void>;
  }> = {};

  public subscribe(marketId: string, callbacks: SubscribeCallbacks<{ fundingRate: Ref<FundingRateData> }>) {
    const getDefaultData = () => ({
      fundingRate: ref({
        time: 0,
        funding_rate: '0',
        mark_price: '0',
      } as FundingRateData),
      listenerCount: 1,
    });

    let marketData = this.data[marketId];

    logger.log(`${this.logPrefix} subscribing to ${marketId}`);

    if (!marketData) {
      marketData = getDefaultData();
      this.data[marketId] = marketData;

      marketData.unsubscribe = this.openChannel(marketId, {
        success: () => {
          const marketDataForId = this.data[marketId];
          if (marketDataForId) {
            callbacks.success({ fundingRate: marketDataForId.fundingRate });
          }
        },
        fail: callbacks.fail,
      });
    } else {
      marketData.listenerCount += 1;
      callbacks.success({ fundingRate: marketData.fundingRate });
    }

    return () => this.unsubscribe(marketId);
  }

  private unsubscribe(marketId: string) {
    const marketData = this.data[marketId];
    if (!marketData) return;

    marketData.listenerCount -= 1;

    if (marketData.listenerCount < 1) {
      marketData.unsubscribe?.().then(() => this.clear(marketId));
    }
  }

  private openChannel(marketId: string, callbacks: SubscribeCallbacks) {
    logger.log(`${this.logPrefix} opening channel for ${marketId}`);

    return fundingRateChannel.subscribe(
      {
        market: marketId,
        onMessage: (e: FundingRateMessage) => {
          if (e?.instrument_code !== marketId) {
            return;
          }

          switch (e.type) {
            case WSIncomingEventTypes.CURRENT_FUNDING_RATE:
            case WSIncomingEventTypes.FUNDING_RATE_UPDATE:
              logger.log(`${this.logPrefix} updating funding rate for ${marketId}:`, e.data);
              this.updateFundingRate(marketId, e.data);
              break;
            default:
              logger.warn(`${this.logPrefix} unknown message type:`, e.type);
              break;
          }
        },
      },
      callbacks,
    );
  }

  private updateFundingRate(marketId: string, fundingRateData: FundingRateData) {
    const marketData = this.data[marketId];
    if (marketData) {
      marketData.fundingRate.value = fundingRateData;
    } else {
      logger.warn(`${this.logPrefix} received update for non-existent market ${marketId}`);
    }
  }

  private clear(marketId: string) {
    logger.info(`${this.logPrefix} deleting ${marketId} data`);
    delete this.data[marketId];
  }
}

const fundingRateService = new FundingRate();

export default fundingRateService;
